import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DiscountCouponService from "./Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import PropTypes from 'prop-types';
import { Col, CardBody, Card } from "reactstrap";
import PermissionAccess from '../../PermissionAccess/Access.js';
import { getFileUrl, uploadFile } from "../../utils/fileUtils";

const EditDiscountCouponModal = ({ showModalEditDiscountCoupon, handleToggleModalEditDiscountCoupon, id, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { authData } = PermissionAccess();

  const [data, setData] = useState({
    title: '',
    sub_title: '',
    features: '',
    price: '',
    description: '',
    image: '',
    status: '',
  });

  const [errors, setErrors] = useState({});

  // Get data
  useEffect(() => {
    if (id && showModalEditDiscountCoupon) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await DiscountCouponService.GetSingleDevice(authData.sessionID, id);
          setLoading(false);

          if (response.data.message === "Unauthorized") {
            AuthService.logout();
            navigate("/login");
            window.location.reload();
          } else if (response.data.status) {
            setData({
              ...response.data.data.data,
              features: response.data.data.data.features.split(','),
            });
          } else {
            Alerts.swalErrorAlert(t("Something went wrong, Please try again."));
          }
        } catch (error) {
          setLoading(false);
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      };

      fetchData();
    }
  }, [id, showModalEditDiscountCoupon]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: value ? '' : `${name} is required` }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    let updatedFeatures = [...data.features];
    if (checked) {
      updatedFeatures.push(name);
    } else {
      updatedFeatures = updatedFeatures.filter((feature) => feature !== name);
    }
    setData({ ...data, features: updatedFeatures });
    setErrors((prevErrors) => ({ ...prevErrors, features: updatedFeatures.length > 0 ? '' : 'Features is required' }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    setLoading(true);
    try {
      const path = await uploadFile(file);
      setData((prevData) => ({ ...prevData, image: path }));
      setErrors((prevErrors) => ({ ...prevErrors, image: '' }));
    } catch (error) {
      Alerts.swalErrorAlert(error.message || t("Image upload failed."));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let validationErrors = {};
    if (!data.title) validationErrors.title = 'Title is required';
    if (!data.sub_title) validationErrors.sub_title = 'Sub title is required';
    if (!data.features.length) validationErrors.features = 'Features is required';
    if (!data.price) validationErrors.price = 'Price is required';
    if (!data.description) validationErrors.description = 'Description is required';
    if (!data.image) validationErrors.image = 'Image is required';
    if (!data.status) validationErrors.status = 'Status is required';

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await DiscountCouponService.EditDevice(authData.sessionID, { ...data, features: data.features.join(',') }, id);
      setLoading(false);

      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else if (response.data.status) {
        handleToggleModalEditDiscountCoupon();
        onSuccess();
        Alerts.swalSuccessAlert(t("Device updated successfully."));
      } else {
        Alerts.swalErrorAlert(response.data.errors[0]?.message || 'An error occurred');
      }
    } catch (error) {
      setLoading(false);
      Alerts.swalErrorAlert(t("Server Error, Please try again later."));
    }
  };

  const handleClose = () => {
    setErrors({});
    handleToggleModalEditDiscountCoupon();
  };

  return (
    <div className={showModalEditDiscountCoupon ? "modal d-block mt-4" : "modal"} style={{ display: showModalEditDiscountCoupon ? 'block' : 'none', backgroundColor: 'rgba(0, 0, 0, 0.5)' }} tabIndex="-1" role="dialog">
      <form onSubmit={handleSubmit}>
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">{t("Edit Device")}</h5>
            </div>
            <div className="modal-body">
              <Col md="12">
                <Card className="main-card mb-3">
                  <CardBody>
                    <LoadingOverlay
                      tag="div"
                      active={loading}
                      styles={{ overlay: (base) => ({ ...base }) }}
                      spinner={<DNALoader />}
                    >


                      <div className="row">

                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="title" className="col-form-label">{t("Title")}:</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Title"
                              name="title"
                              onChange={handleChange}
                              value={data.title}
                            />
                            {errors.title && <strong className="error text-danger">{errors.title}</strong>}
                          </div>
                        </div>


                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="sub_title" className="col-form-label">{t("Sub Title")}:</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Sub Title"
                              name="sub_title"
                              onChange={handleChange}
                              value={data.sub_title}
                            />
                            {errors.sub_title && <strong className="error text-danger">{errors.sub_title}</strong>}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="features" className="col-form-label">{t("Features")}:</label>
                            <div>
                              {['Mobile App', 'Bluetooth', 'Rechargable'].map((feature) => (
                                <div key={feature} className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={feature}
                                    name={feature}
                                    checked={data.features.includes(feature)}
                                    onChange={handleCheckboxChange}
                                  />
                                  <label className="form-check-label" htmlFor={feature}>{t(feature)}</label>
                                </div>
                              ))}
                            </div>
                            {errors.features && <strong className="error text-danger">{errors.features}</strong>}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="price" className="col-form-label">{t("Price")}:</label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Enter Price"
                              name="price"
                              onChange={handleChange}
                              value={data.price}
                            />
                            {errors.price && <strong className="error text-danger">{errors.price}</strong>}
                          </div>
                        </div>

                        

                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="image" className="col-form-label">{t("Image")}:</label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={handleImageChange}
                            />
                            {data.image && (
                              <img
                                src={getFileUrl(data.image)}
                                alt={data.title}
                                style={{ width: "60px", height: "60px", marginTop: "8px", borderRadius: "8px", objectFit: "cover" }}
                              />
                            )}
                            {errors.image && <strong className="error text-danger">{errors.image}</strong>}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="status" className="col-form-label">{t("Status")}:</label>
                            <select
                              className="form-control"
                              name="status"
                              onChange={handleChange}
                              value={data.status}
                            >
                              <option value="">{t("Select Status")}</option>
                              <option value="Active">{t("Active")}</option>
                              <option value="InActive">{t("InActive")}</option>
                            </select>
                            {errors.status && <strong className="error text-danger">{errors.status}</strong>}
                          </div>
                        </div>

                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="description" className="col-form-label">{t("Description")}:</label>
                            <textarea
                              className="form-control"
                              placeholder="Enter Description"
                              name="description"
                              style={{ height: '120px' }}
                              onChange={handleChange}
                              value={data.description}
                            />
                            {errors.description && <strong className="error text-danger">{errors.description}</strong>}
                          </div>
                        </div>

                        <div className="text-center mt-4">
                          <button type="button" className="btn btn-outline-light btn-hover-shine me-3" onClick={handleClose}>{t("Close")}</button>
                          <button type="submit" className="btn btn-custom-color">{t("Save")}</button>
                        </div>
                      </div>

                    </LoadingOverlay>
                  </CardBody>
                </Card>
              </Col>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

EditDiscountCouponModal.propTypes = {
  showModalEditDiscountCoupon: PropTypes.bool.isRequired,
  handleToggleModalEditDiscountCoupon: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default EditDiscountCouponModal;
